import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gamepad2 } from "lucide-react";
import { getFlagsGallery } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { SectionHeading } from "@/components/home/ExploreByState";

export default function FlagsGallery() {
  const [flags, setFlags] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFlagsGallery().then((d) => setFlags(d.flags)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? flags.filter((f) => f.name.toLowerCase().includes(t)) : flags;
  }, [flags, q]);

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-12">
      <Seo
        title="Flags of America — All 50 States, D.C. & Territories"
        path="/flags"
        description="Browse the official flags of all 50 U.S. states, Washington, D.C. and the U.S. territories. Every flag is a public-domain government symbol — click any to open its state profile."
      />
      <SectionHeading kicker="Flags of America" title="Every official U.S. flag, in one place" />
      <div className="-mt-4 mb-6 flex flex-wrap items-center gap-4">
        <p className="max-w-2xl text-muted-foreground">
          <T>The official flags of all 50 states, Washington, D.C. and the U.S. territories — public-domain government symbols. Click any flag to open its full profile.</T>
        </p>
        <Link to="/flags/quiz" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-oldglory-red px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-oldglory-red/90" data-testid="flags-quiz-cta">
          <Gamepad2 className="h-4 w-4" /> <T>Play the Flag Quiz</T>
        </Link>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter flags…"
        className="mb-8 w-full max-w-sm rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus:border-navy"
        data-testid="flags-filter"
      />

      {loading ? (
        <p className="animate-pulse font-display text-lg text-navy">Loading flags…</p>
      ) : (
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="flags-grid">
          {list.map((f, i) => (
            <motion.div key={f.geo_id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 10) * 0.03 }}>
              <Link to={`/place${f.url_path}`} className="group block overflow-hidden rounded-xl border border-border bg-white transition-all hover:-translate-y-1 hover:shadow-xl hover:ring-1 hover:ring-navy/30" data-testid={`flag-card-${f.geo_id}`}>
                <div className="flex h-32 items-center justify-center bg-parchment/40 p-3">
                  <img src={f.flag_img} alt={`Flag of ${f.name}`} loading="lazy" className="max-h-full max-w-full object-contain shadow-sm ring-1 ring-black/5 transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="px-3 py-2.5">
                  <div className="font-display text-sm font-bold text-navy group-hover:text-oldglory-red">{f.name}</div>
                  {f.nickname && <div className="truncate text-[11px] text-muted-foreground">{f.nickname}</div>}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
